import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { setDynamicSeries } from 'store/actions/dynamicSeries';
import {
  resetFoundLayerAction,
  setCurrentLayerAction,
  setDefaultLayerAction,
} from 'store/actions/layers';
import { LayersNestedListItemProps } from 'types/mainPage';
import { LayersNestedList } from '../LayersNestedList';
import { getAllChildren } from 'components/LayersTab/constants';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { TooltipMapIcon } from 'components/UI/TooltipMapIcon';
import {
  ListItemButton,
  ListItemText,
  Collapse,
  List,
  Box,
  ListItemIcon,
  Radio,
} from '@mui/material';

export const LayersNestedListItem: React.FC<LayersNestedListItemProps> = ({
  title,
  children,
  nesting,
  system_name,
  layerId,
  url,
  legendUrl,
  mapFileUrl,
  parent,
  tooltipText,
  firstParent,
  legend_size,
  dynamic_series,
  scale_1_50,
  scale_1_50_vector,
  meta_file,
}): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const { currentLayer, allLayers, defaultLayer, currentLayerId } =
    useAppSelector((state) => state.layers);
  const indent = 2 * nesting;
  const dispatch = useAppDispatch();

  const handleClick = (event: any) => {
    if (system_name) {
      dispatch(
        setCurrentLayerAction(
          system_name,
          url,
          layerId,
          legendUrl,
          mapFileUrl,
          meta_file,
          parent,
          title,
          legend_size,
          scale_1_50,
          scale_1_50_vector
        )
      );
      dispatch(setDynamicSeries(dynamic_series));
      dispatch(resetFoundLayerAction());
    } else {
      setOpen(!open);
    }
  };

  useEffect(() => {
    if (children) {
      const newCurrentLayer = children.find(
        (item) => item.id === currentLayerId
      );

      if (newCurrentLayer) {
        dispatch(
          setCurrentLayerAction(
            newCurrentLayer.layer,
            newCurrentLayer.url,
            newCurrentLayer.id,
            newCurrentLayer.legend_file,
            newCurrentLayer.map_file,
            newCurrentLayer.meta_file,
            parent,
            newCurrentLayer.name,
            newCurrentLayer.legend_size,
            newCurrentLayer.scale_1_50,
            newCurrentLayer.scale_1_50_vector
          )
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!defaultLayer || !children) return;

    if (defaultLayer === Number(firstParent)) {
      setOpen(true);
    }
    if (!firstParent) {
      dispatch(setDefaultLayerAction(0));
    }
  }, [defaultLayer]);

  useEffect(() => {
    if (currentLayerId && children) {
      const allChildren = getAllChildren(children).map((item) => item.id);
      if (allChildren.includes(currentLayerId)) {
        setOpen(true);
      } else if (open) {
        setOpen(false);
      }
    }
  }, [currentLayerId]);

  return (
    <Box>
      <ListItemButton sx={{ pl: indent }} onClick={handleClick}>
        {children ? (
          <ListItemIcon sx={{ minWidth: 32 }}>
            {open ? (
              <ArrowDropDownIcon fontSize={'large'} />
            ) : (
              <ArrowRightIcon fontSize={'large'} />
            )}
          </ListItemIcon>
        ) : (
          <Radio
            checked={currentLayer === system_name}
            name="radio-button-demo"
            inputProps={{ 'aria-label': 'A' }}
          />
        )}
        <TooltipMapIcon
          title={tooltipText || ''}
          placement="right"
          followCursor
        >
          <ListItemText primary={title} />
        </TooltipMapIcon>
      </ListItemButton>

      {children && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <LayersNestedList
              list={children}
              nesting={nesting + 1}
              parent={parent}
            />
          </List>
        </Collapse>
      )}
    </Box>
  );
};
